import { useParams, useNavigate } from 'react-router-dom'
import { useAnnouncement } from '@/hooks/useAnnouncement'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { ArrowLeft, AlertTriangle, Megaphone, Pin } from 'lucide-react'
import { format } from 'date-fns'
import { Button } from '@/components/ui/button'

export function AnnouncementDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data: announcement, isLoading } = useAnnouncement(id)

  if (isLoading)
    return (
      <div className="p-4 flex items-center justify-center min-h-[200px]">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    )

  if (!announcement)
    return (
      <div className="p-4 text-center">
        <p>Announcement not found.</p>
        <Button onClick={() => navigate('/')} className="mt-4">
          Back to Home
        </Button>
      </div>
    )

  const PRIORITY_BADGES = {
    urgent: { Icon: AlertTriangle, label: 'URGENT', className: 'bg-red-600 text-white' },
    important: { Icon: Pin, label: 'IMPORTANT', className: 'bg-amber-500 text-white' },
    normal: {
      Icon: Megaphone,
      label: 'ANNOUNCEMENT',
      className: 'bg-primary text-primary-foreground',
    },
  }
  const badge = PRIORITY_BADGES[announcement.priority as keyof typeof PRIORITY_BADGES]
  const Icon = badge.Icon

  return (
    <div className="min-h-screen pb-20">
      <div className="sticky top-0 bg-background/95 backdrop-blur z-10 border-b">
        <div className="px-4 py-3 flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} aria-label="Go back">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="font-semibold truncate flex-1">Announcement</h1>
        </div>
      </div>

      <article className="px-4 py-6 space-y-4">
        <div className="flex items-center gap-2 text-xs flex-wrap">
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold ${badge.className}`}
          >
            <Icon className="h-3 w-3" />
            {badge.label}
          </span>
          {announcement.category && (
            <span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
              {announcement.category}
            </span>
          )}
        </div>

        <h1 className="text-2xl font-bold leading-tight">{announcement.title}</h1>

        <div className="flex items-center justify-between text-sm text-muted-foreground border-y py-2">
          <span>From: {announcement.author_entity_name}</span>
          <span>{format(new Date(announcement.published_at), 'PPp')}</span>
        </div>

        {announcement.image_url && (
          <img src={announcement.image_url} alt="" className="w-full rounded-lg" />
        )}

        <div className="prose prose-sm dark:prose-invert max-w-none">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{announcement.body}</ReactMarkdown>
        </div>
      </article>
    </div>
  )
}
